/**
 * Potential Duplicate Product Exception Config (per-group view)
 *
 * Covers: ex-product-ls-duplicate (shown to users as "Potential Duplicates")
 *
 * Server aggregates tags into one row per duplicate group; each row carries a
 * compact `members` array plus top-level summary fields. The "Compare
 * Duplicates" button (or row click) opens `DuplicateGroupCompareDialog`,
 * which fetches the full per-member detail for the side-by-side comparison.
 */
import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import CountChip, { CountChipItem } from '../../../components/inspector/shared/CountChip'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, skuPart, textPart } from '../shared/rowSubtitle'
import { formatMainCategory } from '../../../utils/productCategory'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

interface GroupMember {
  ls_id?: string
  sku?: string | null
  name?: string | null
  active?: boolean | null
  last_sold?: string | null
  total_stock?: number | null
  price_including_tax?: number | null
  score?: number | null
  lightspeed_product_link?: string | null
}

const scoreColor = (
  score: number | null,
): 'error' | 'warning' | 'default' => {
  if (score === null) return 'default'
  if (score >= 75) return 'error'
  return 'warning'
}

const renderMatchScore = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const score = typeof row.score === 'number' ? row.score : null
  const label = score !== null ? `${score}%` : '-'
  return React.createElement(Chip, {
    size: 'small',
    variant: 'filled',
    label,
    color: scoreColor(score),
    sx: { ...NUMERIC_FONT_SX, fontWeight: 700, minWidth: 56 },
  })
}

const renderProductCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const members: GroupMember[] = Array.isArray(row.members) ? row.members : []
  const items: CountChipItem[] = members.map((m, idx) => ({
    id: m.ls_id || `${row.group_id}-${idx}`,
    primary: m.name || m.sku || '-',
    secondary: m.sku || null,
    metrics: [
      typeof m.score === 'number' ? `${m.score}%` : '-',
      m.last_sold ? formatDate(m.last_sold) : '-',
      m.price_including_tax != null ? formatCurrency(m.price_including_tax) : '-',
    ],
    link: m.lightspeed_product_link || undefined,
  }))
  return React.createElement(CountChip, {
    count: row.member_count || members.length,
    items,
    label: 'Products in group',
    metricHeaders: ['Score', 'Last sold', 'Price'],
  })
}

const renderGroupName = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const name: string = row.representative_name || '-'
  const extra = Math.max(0, (row.member_count || 0) - 1)
  return React.createElement(
    Stack,
    { direction: 'row', alignItems: 'center', spacing: 1 },
    React.createElement(
      Typography,
      {
        variant: 'body2',
        sx: {
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 360,
        },
        title: name,
      },
      name,
    ),
    extra > 0 &&
      React.createElement(Chip, {
        size: 'small',
        variant: 'outlined',
        label: `+${extra}`,
        sx: { height: 20, fontSize: 11, fontWeight: 600 },
      }),
  )
}

const renderMatchReasons = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const reasons: string[] = Array.isArray(row.match_reasons) ? row.match_reasons : []
  if (!reasons.length) return '-'
  const chips = reasons.map((reason, i) =>
    React.createElement(Chip, {
      key: `${reason}-${i}`,
      size: 'small',
      variant: 'outlined',
      label: reason.replace(/_/g, ' '),
      sx: { height: 20, fontSize: 11 },
    }),
  )
  return React.createElement(
    Box,
    { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 } },
    ...chips,
  )
}

const renderLastSoldRange = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const oldest: string | null = row.oldest_last_sold || null
  const newest: string | null = row.newest_last_sold || null
  if (!oldest && !newest) return '-'
  if (oldest && newest && oldest !== newest) {
    return `${formatDate(oldest)} – ${formatDate(newest)}`
  }
  return formatDate(oldest || newest || '')
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'score',
    label: 'Match',
    align: 'center',
    contexts: ['inspector'],
    render: renderMatchScore,
    getSortValue: (row) => Number(row.score) || 0,
  },
  {
    key: 'representative_name',
    label: 'Name',
    align: 'left',
    minWidth: 220,
    contexts: ['inspector'],
    render: renderGroupName,
    getSortValue: (row) => row.representative_name || '',
  },
  {
    key: 'member_count',
    label: 'Products',
    align: 'center',
    type: 'integer',
    contexts: ['inspector'],
    render: renderProductCount,
    getSortValue: (row) => Number(row.member_count) || 0,
    exportFormat: 'text',
  },
  {
    key: 'match_reasons',
    label: 'Match reasons',
    contexts: ['inspector'],
    render: renderMatchReasons,
    getSortValue: (row) =>
      Array.isArray(row.match_reasons) ? row.match_reasons.join(',') : '',
  },
  {
    key: 'total_stock',
    label: 'Total stock',
    align: 'right',
    type: 'integer',
    contexts: ['inspector'],
  },
  {
    key: 'oldest_last_sold',
    label: 'Last sold',
    contexts: ['inspector'],
    render: renderLastSoldRange,
    getSortValue: (row) => row.newest_last_sold || '',
    exportFormat: 'date',
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  { key: 'score', label: 'Match %', type: 'integer', align: 'right', contexts: ['platform'] },
  { key: 'band', label: 'Band', contexts: ['platform'] },
  { key: 'representative_name', label: 'Name', contexts: ['platform'] },
  { key: 'member_count', label: '# Products', type: 'integer', align: 'right', contexts: ['platform'] },
  { key: 'member_skus_display', label: 'Member SKUs', contexts: ['platform'] },
  { key: 'member_names_display', label: 'Member names', contexts: ['platform'] },
  { key: 'match_reasons_display', label: 'Match reasons', contexts: ['platform'] },
  { key: 'total_stock', label: 'Total stock', type: 'integer', align: 'right', contexts: ['platform'] },
  { key: 'oldest_last_sold', label: 'Oldest last sold', type: 'date', contexts: ['platform'], exportFormat: 'date' },
  { key: 'newest_last_sold', label: 'Newest last sold', type: 'date', contexts: ['platform'], exportFormat: 'date' },
]

export const duplicateConfig: ExceptionConfig = {
  codes: ['ex-product-ls-duplicate'],
  columns: [...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'score',
  defaultSortDirection: 'desc',
  complexity: 'comparison',
  rowKeyField: 'row_key',
  getRowSubtitle: row =>
    composeRowSubtitle([
      skuPart(row.sku),
      typeof row.member_count === 'number' && row.member_count > 0
        ? `${row.member_count} duplicates`
        : null,
      row.score != null ? `score ${row.score}` : null,
      textPart(formatMainCategory(row.category_root_name)),
    ]),
  // Side-by-side group comparison lives in DuplicateGroupCompareDialog -
  // opened by the per-row "Compare Duplicates" button or a row click
  // (replaces the old inline expandable rendering).
  rowDetail: {
    type: 'duplicate-group',
    headerLabel: 'Actions',
    groupKind: 'product',
    getGroupId: (row) => row.group_id ?? null,
    getDialogTitle: (row) => row.representative_name || undefined,
    rowClick: true,
  },
}
